'use client';
import { Address, Province } from '@/api/types/public';
import { useStore } from '@/providers/app-store-provider';
import { font } from '@/theme/zeelool.theme';
import { EPlatform } from '@/utils/enums/platform-enum';
import { env } from '@/utils/env';
import {
  cityValidator,
  firstnameValidator,
  getErrorTranslationKey,
  lastnameValidator,
} from '@/utils/form-rule';
import { hasSpecialChar, parsePhoneValue } from '@/utils/tools';
import { Box, ButtonOwnProps } from '@mui/material';
import { omit } from 'lodash-es';
import { useTranslations } from 'next-intl';
import Script from 'next/script';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Button from '../Button';
import Input from '../Form/FormInput';
import PhoneInput from '../Form/PhoneInput';
import TaxIDInput from '../Form/TaxIDInput';
import ZipCodeInput from '../Form/ZipCodeInput';
import { ResponsiveContext } from '../Responsive/ResponsiveProvider';
import WapOnly from '../Responsive/WapOnly';
import WebOnly from '../Responsive/WebOnly';
import FormItemApartment from './FormItemAparment';
import FormItemCountry from './FormItemCountry';
import FormItemProvince from './FormItemProvince';
import FormItemSetDefault from './FormItemSetDefault';
import FormItemStreet from './FormItemStreet';
import ProvinceDrawer from './MobileProvinceDrawer';

export interface AddressFormData {
  firstname: string;
  lastname: string;
  country: string;
  country_id: string;
  street: string;
  apartment: string;
  city: string;
  province: string;
  province_id: string | number;
  postcode: string;
  telCode: string;
  telephone: string;
  cpf: string;
  default: number;
  region_code: string;
}

export default function AddressForm({
  data = {
    firstname: '',
    lastname: '',
    country_id: env.NEXT_PUBLIC_ADDRESS_COUNTRY_ID,
    country: env.NEXT_PUBLIC_ADDRESS_COUNTRY,
    street: '',
    apartment: '',
    city: '',
    province: '',
    province_id: '',
    postcode: '',
    tel_code: env.NEXT_PUBLIC_ADDRESS_TEL_CODE,
    tel_country: env.NEXT_PUBLIC_ADDRESS_TEL_COUNTRY,
    telephone: '',
    cpf: '', // 巴西税号
    default: 2,
    region_code: '',
  },
  type = 'ADD',
  bizType = 'shopping',
  close = () => {},
  onSubmit = () => {},
  saveBtnSxPc = {},
  loading = false,
  showDefault = true,
  showCancel = true,
  disabledCountry = false,
  isNeedPagePadding = true,
  stateAndZipLayout = 'horizontal',
}: {
  data: Address;
  type?: 'ADD' | 'EDIT';
  bizType?: 'shopping' | 'billing';
  showDefault?: boolean;
  showCancel?: boolean;
  saveBtnSxPc?: ButtonOwnProps['sx'];
  close?: () => void;
  onSubmit?: (address?: Address) => void;
  loading?: boolean;
  disabledCountry?: boolean;
  isNeedPagePadding?: boolean;
  stateAndZipLayout?: 'horizontal' | 'vertical'; // 用来控制在pc端，这两个是两行展示还是一行展示
}) {
  const t = useTranslations('address');
  const tValidation = useTranslations('components.address.validation');
  const getTelCode = () => {
    if (!data.tel_code) {
      return '';
    }
    if (data.tel_country && data.tel_code) {
      return `${data.tel_country}(+${data.tel_code})`;
    }
    return `(+${data.tel_code})`;
  };
  const formData = useForm({
    defaultValues: {
      firstname: data.firstname,
      lastname: data.lastname,
      country: data.country,
      country_id: data.country_id,
      street: data.street,
      apartment: data.apartment,
      city: data.city,
      province: data.province,
      province_id: data.province_id,
      postcode: data.postcode,
      telCode: getTelCode(),
      telephone: data.telephone,
      cpf: data.cpf,
      default: data.default,
      region_code: data.region_code,
    },
    mode: 'all',
  });
  const { deviceType } = useContext(ResponsiveContext);
  const [provinceList, setProvinceList] = useState<Province[]>([]);
  const [showProvinceDialog, setShowProvinceDialog] = useState(false);
  const countryList = useStore((state) => state.countryList);
  // 是否操作过电话号码，操作过之后不再自动改变
  const [telCodeSelected, setTelCodeSelected] = useState(false);
  const getProvinceList = (_country_id: string) => {
    const _country = countryList.find((__country) => __country.country_id === _country_id);
    if (_country) {
      setProvinceList(_country.province || []);
    }
  };

  useEffect(() => {
    getProvinceList(formData.getValues('country_id'));
    if (type !== 'ADD') {
      formData.trigger();
    }
  }, []);

  const handleOnSubmit = async () => {
    let isValid = await formData.trigger();
    if (!isValid) {
      return;
    }
    const addressForm = formData.getValues();
    // 当前处理只针对美国：
    // 传province不传province_id的时候，去匹配Z站地址库中的province是否存在，不存在的时候进行拦截，清空已填入的province内容（只在创建地址，更新地址，支付成功页更改地址位置校验）。
    // 前端在创建地址，更新地址，支付成功页地址编辑，payment页面老地址拦截，4处变更。
    if (addressForm.country_id === 'US' && addressForm.province && !addressForm.province_id) {
      const flag = provinceList.some((item) => item.name === addressForm.province);
      if (!flag) {
        formData.setValue('province', '');
        formData.setValue('province_id', '');
        isValid = await formData.trigger();
      }
    }
    if (isValid) {
      const { telephone, telCode } = parsePhoneValue(addressForm.telephone);
      onSubmit({
        ...data,
        ...omit(addressForm, ['telCode', 'telephone']),
        province_id: String(addressForm.province_id), // 确保 province_id 是字符串
        tel_country: telCode.split('(').length === 2 ? telCode.split('(')[0] : undefined,
        tel_code: telCode.replace(/\D/g, ''),
        telephone,
      });
    }
  };

  const telCode = useWatch({
    control: formData.control,
    name: 'telCode',
  });

  const lastname = useWatch({
    control: formData.control,
    name: 'lastname',
  });

  const firstname = useWatch({
    control: formData.control,
    name: 'firstname',
  });

  useEffect(() => {
    const _firstname = hasSpecialChar(firstname);
    if (_firstname !== firstname) {
      formData.setValue('firstname', _firstname);
    }
    if (lastname) {
      formData.trigger(['lastname']);
    }
  }, [firstname]);

  useEffect(() => {
    const _lastname = hasSpecialChar(lastname);
    if (_lastname !== lastname) {
      formData.setValue('lastname', _lastname);
    }
    if (firstname) {
      formData.trigger(['firstname']);
    }
  }, [lastname]);
  return (
    <>
      <Script
        strategy="afterInteractive"
        src="https://maps.googleapis.com/maps/api/js?key=AIzaSyCX6tjfZXl-IGm-WzLNCaZQRtuaorUoGnw&libraries=places&language=en"
      />
      <WapOnly>
        <ProvinceDrawer
          show={showProvinceDialog}
          close={() => setShowProvinceDialog(false)}
          data={provinceList}
          onSelect={() => {}}
        />
      </WapOnly>
      <FormProvider {...formData}>
        <Box
          component="form"
          autoComplete="off"
          noValidate
          sx={{
            // '& .MuiBox-root': {
            //   marginBottom: deviceType === EPlatform.WEB ? '10px !important' : '8px !important',
            // },
            // TODO 下面的间距被 vw 给覆盖了, 暂时无解，等着改插件
            '& .relative.mb-\\[32px\\]': {
              marginBottom: deviceType === EPlatform.WEB ? '10px !important' : '8px !important',
            },
            '& .relative.mb-\\[24px\\]': {
              marginBottom: deviceType === EPlatform.WEB ? '10px !important' : '8px !important',
            },
            '& .relative.flex.items-baseline': {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            },
            '& .absolute.left-0.top-full.form-error-text, & .absolute.left-0.top-full.text-error': {
              position: 'relative',
              textWrap: 'unset',
              lineHeight: '14px',
            },
          }}
          className={`flex flex-row ${isNeedPagePadding ? '!pl-16 !pr-16' : ''} flex-wrap`}
        >
          <Box
            sx={{
              width:
                deviceType === EPlatform.WEB && bizType !== 'billing' ? 'calc(50% - 12px)' : '100%',
            }}
          >
            <Input
              name="firstname"
              label={t('firstName')}
              required
              control={formData.control}
              rules={{
                validate: () => {
                  const errorCode = firstnameValidator(
                    formData.getValues('firstname'),
                    formData.getValues('lastname'),
                  );
                  if (errorCode) {
                    const translationKey = getErrorTranslationKey(errorCode);
                    return translationKey ? tValidation(translationKey) : 'Validation error';
                  }
                  return true;
                },
              }}
            />
          </Box>
          <Box
            sx={{
              width:
                deviceType === EPlatform.WEB && bizType !== 'billing' ? 'calc(50% - 12px)' : '100%',
              marginLeft: deviceType === EPlatform.WEB && bizType !== 'billing' ? '24px' : '0',
            }}
          >
            <Input
              name="lastname"
              label={t('lastName')}
              required
              control={formData.control}
              rules={{
                validate: () => {
                  const errorCode = lastnameValidator(
                    formData.getValues('lastname'),
                    formData.getValues('firstname'),
                  );
                  if (errorCode) {
                    const translationKey = getErrorTranslationKey(errorCode);
                    return translationKey ? tValidation(translationKey) : 'Validation error';
                  }
                  return true;
                },
              }}
            />
          </Box>
          <FormItemCountry
            formData={formData}
            countryList={countryList}
            setProvinceList={setProvinceList}
            telCodeSelected={telCodeSelected}
            disabled={disabledCountry}
          />
          <FormItemStreet formData={formData} countryList={countryList} />
          <FormItemApartment formData={formData} />
          <Box sx={{ width: '100%' }}>
            <Input
              name="city"
              label={t('city')}
              required
              control={formData.control}
              slotProps={{
                input: {
                  inputProps: {
                    maxLength: 50,
                    autoComplete: 'off',
                  },
                },
              }}
              rules={{
                validate: () => {
                  const errorCode = cityValidator(formData.getValues('city'));
                  if (errorCode) {
                    const translationKey = getErrorTranslationKey(errorCode);
                    return translationKey ? tValidation(translationKey) : 'Validation error';
                  }
                  return true;
                },
              }}
            />
          </Box>
          <div
            className={`w-full flex ${stateAndZipLayout === 'vertical' ? 'flex-col' : 'flex-row'}`}
          >
            <FormItemProvince
              formData={formData}
              provinceList={provinceList}
              stateAndZipLayout={stateAndZipLayout}
            />
            <Box
              sx={{
                width:
                  [EPlatform.WEB].includes(deviceType) && stateAndZipLayout === 'horizontal'
                    ? 'calc(50% - 12px)'
                    : '100%',
                marginLeft: (() => {
                  if (bizType === 'billing') {
                    return '0px';
                  }
                  if (![EPlatform.WEB].includes(deviceType) || stateAndZipLayout !== 'horizontal') {
                    return '12px';
                  } else {
                    return '24px';
                  }
                })(),
              }}
            >
              <ZipCodeInput
                name="postcode"
                label={t('zipPostalCode')}
                required
                control={formData.control}
                countryId={formData.getValues('country_id')}
                placeholder=""
              />
            </Box>
          </div>
          {['MX', 'BR'].includes(formData.getValues('country_id')) && (
            <Box sx={{ width: '100%' }}>
              <TaxIDInput
                name="cpf"
                control={formData.control}
                countryId={formData.getValues('country_id')}
                required
              />
            </Box>
          )}
          <Box sx={{ width: '100%' }}>
            <PhoneInput
              name="telephone"
              label={t('telephone')}
              placeholder={t('telephonePlaceholder')}
              required
              control={formData.control}
              countryList={countryList}
              telCode={telCode}
              defaultValue={getTelCode() + data.telephone}
              onChange={() => {
                if (!telCodeSelected) {
                  setTelCodeSelected(true);
                }
              }}
            />
          </Box>
        </Box>
      </FormProvider>
      {showDefault && (
        <FormItemSetDefault formData={formData} isNeedPagePadding={isNeedPagePadding} />
      )}
      {deviceType !== EPlatform.WEB && <div className="h-10" />}
      <WebOnly>
        <div
          className={`${isNeedPagePadding ? 'pl-16 pr-16' : ''} mt-18 flex ${showCancel ? 'justify-between' : 'justify-center'} gap-[24px]`}
        >
          {showCancel && (
            <Button
              className="flex-1/2 h-60"
              radiusSize="small"
              color="primary"
              variant="outlined"
              sx={{
                fontSize: '16px',
                fontFamily: font.bb,
              }}
              onClick={close}
              loading={loading}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            className="flex-1/2 h-60"
            radiusSize="small"
            color="primary"
            variant="contained"
            sx={{
              fontSize: '16px',
              fontFamily: font.bb,
              ...saveBtnSxPc,
            }}
            loading={loading}
            onClick={() => {
              handleOnSubmit();
            }}
          >
            {t('save')}
          </Button>
        </div>
      </WebOnly>
      <WapOnly>
        <div
          className={`AddressFormFooter sticky z-2 bottom-0 flex h-60 pl-12 pr-12 justify-between items-center gap-10 bg-white shadow-[0_0_5px_0_#c0c0c080] ${
            !showCancel ? '!pl-0 !pr-0 shadow-none' : ''
          }`}
        >
          {showCancel && (
            <Button
              className="flex-1/2 h-40"
              radiusSize="large"
              color="primary"
              variant="outlined"
              sx={{
                fontSize: '16px',
                fontFamily: font.bb,
              }}
              onClick={close}
              loading={loading}
            >
              {t('cancel')}
            </Button>
          )}
          <Button
            className="flex-1/2 h-40"
            radiusSize="large"
            color="primary"
            variant="contained"
            sx={{
              fontSize: '16px',
              fontFamily: font.bb,
            }}
            loading={loading}
            onClick={() => {
              handleOnSubmit();
            }}
          >
            {t('save')}
          </Button>
        </div>
      </WapOnly>
    </>
  );
}
